import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches } from '../branchesApi';
import {
  createDepartment,
  fetchDepartmentById,
  fetchDepartmentManagerCandidates,
  fetchDepartments,
  mapDepartmentFormToPayload,
  updateDepartment,
} from '../departmentsApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';
import './DecisionFormPage.css';

const EMPTY = {
  branchId: '',
  parentDepartmentId: '',
  name: '',
  description: '',
  managerUserId: '',
  isActive: true,
};

function collectDescendants(flatRows, rootId) {
  const children = {};
  flatRows.forEach((r) => {
    const p = r.parentDepartmentId === '' ? null : Number(r.parentDepartmentId);
    if (!p || !Number.isFinite(p)) return;
    if (!children[p]) children[p] = [];
    children[p].push(r.id);
  });
  const out = new Set();
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop();
    (children[id] || []).forEach((c) => {
      if (!out.has(c)) {
        out.add(c);
        stack.push(c);
      }
    });
  }
  return out;
}

export default function DepartmentFormPage({ mode }) {
  const { direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);
  const invalidEditId = !isNew && (!Number.isFinite(editId) || editId < 1);

  const [branches, setBranches] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [managerOpts, setManagerOpts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setLoadError(t('common.noPerm')); setLoading(false); return; }
    if (invalidEditId) { setLoadError(t('departments.invalidId')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const [companyRows, deptRows] = await Promise.all([fetchBranches(), fetchDepartments()]);
        if (cancelled) return;
        setBranches(companyRows);
        setAllDepartments(deptRows);
        const firstBranchId = companyRows[0]?.id ? String(companyRows[0].id) : '';
        if (isNew) {
          setForm({ ...EMPTY, branchId: firstBranchId });
          if (firstBranchId) {
            const mgr = await fetchDepartmentManagerCandidates(Number(firstBranchId));
            if (!cancelled) setManagerOpts(mgr);
          } else setManagerOpts([]);
        } else {
          const row = await fetchDepartmentById(editId);
          if (cancelled) return;
          setForm({
            branchId: row.branchId,
            parentDepartmentId: row.parentDepartmentId || '',
            name: row.name,
            description: row.description || '',
            managerUserId: row.managerUserId || '',
            isActive: !!row.isActive,
          });
          if (row.branchId) {
            const mgr = await fetchDepartmentManagerCandidates(Number(row.branchId));
            if (!cancelled) setManagerOpts(mgr);
          } else setManagerOpts([]);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('departments.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId, invalidEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cid = form.branchId;
    if (!cid) {
      setManagerOpts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const mgr = await fetchDepartmentManagerCandidates(Number(cid));
        if (!cancelled) setManagerOpts(mgr);
      } catch {
        if (!cancelled) setManagerOpts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.branchId]);

  const excludedParentIds = useMemo(() => {
    if (isNew || !editId) return new Set();
    const s = new Set(collectDescendants(allDepartments, editId));
    s.add(editId);
    return s;
  }, [isNew, editId, allDepartments]);

  const parentCandidates = useMemo(() => allDepartments.filter((d) => {
    if (String(d.branchId) !== String(form.branchId)) return false;
    return !excludedParentIds.has(d.id);
  }), [allDepartments, form.branchId, excludedParentIds]);

  const handleChange = (e) => {
    setErrors((p) => ({ ...p, [e.target.name]: '', _api: '' }));
    if (e.target.name === 'branchId') {
      setForm((p) => ({ ...p, branchId: e.target.value, parentDepartmentId: '' }));
      return;
    }
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = t('departments.valName');
    if (!form.branchId) errs.branchId = t('departments.valBranch');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = mapDepartmentFormToPayload(form);
      if (isNew) await createDepartment(payload);
      else await updateDepartment(editId, payload);
      navigate(`${basePath}/departments`);
    } catch (e) {
      setErrors({ _api: e instanceof Error ? e.message : t('departments.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const fillSample = () => {
    setErrors({});
    setForm((p) => ({
      ...p,
      name: 'إدارة العمليات — عينة',
      description: 'وصف تجريبي: يضم وحدة تصميم العمليات، ضبط الجودة، ومتابعة مؤشرات الأداء. استبدل هذا النص قبل الاعتماد.',
    }));
  };

  return (
    <div className="decision-v2 decision-v2--layout-full" dir={direction}>
      <div className="page-wrapper">
        {loadError && <div className="v2-error">{loadError}</div>}
        {loading && <div className="v2-loading">{t('departments.loading')}</div>}
        {!loading && !loadError && (
          <div className="card">
            <div className="card-top">
              <div className="top-bar top-bar--single-line">
                <div className="page-title-row">
                  <h1 className="page-title">{isNew ? t('departments.titleNew') : t('departments.titleEdit')}<span className="page-title-icon"><Icon name="board" size={16} color="#0EA5A0" /></span></h1>
                </div>
                <div className="action-btns">
                  <button type="button" className="btn btn-outline" onClick={fillSample}>{t('branches.fillSample')}</button>
                  <button type="button" className="btn btn-ghost" onClick={() => navigate(`${basePath}/departments`)}>{t('common.cancel')}</button>
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('departments.saving') : isNew ? t('departments.addBtn') : t('departments.saveBtn')}</button>
                </div>
              </div>
              <p style={{ margin: '4px 0 0', color: '#607d8b', fontSize: '0.88rem' }}>{t('departments.subtitle')}</p>
            </div>
            <div className="card-inner">
              <div className="mform">
                {errors._api && <div style={{ marginBottom: 12, padding: '10px 12px', background: '#ffebee', color: '#c62828', borderRadius: 8 }}>{errors._api}</div>}
                <div className="mform__row mform__row--4">
                  <div className="mform__field mform__field--span2">
                    <label className="mform__label">{t('departments.lblName')} <span>*</span></label>
                    <div className="mform__input-wrap">
                      <Icon name="board" size={14} color="#02c3a6" className="mform__input-icon" />
                      <input className={`mform__input mform__input--with-icon${errors.name ? ' mform__input--error' : ''}`} name="name" value={form.name} onChange={handleChange} />
                    </div>
                    {errors.name && <span className="mform__error">{errors.name}</span>}
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('departments.lblBranch')} <span>*</span></label>
                    <div className="mform__input-wrap">
                      <Icon name="branch" size={14} color="#1b8eff" className="mform__input-icon" />
                      <select className={`mform__select mform__input--with-icon${errors.branchId ? ' mform__select--error' : ''}`} name="branchId" value={form.branchId} onChange={handleChange}>
                        <option value="">{t('board.pickBranch')}</option>
                        {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </div>
                    {errors.branchId && <span className="mform__error">{errors.branchId}</span>}
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('common.status')}</label>
                    <div className="mform__input-wrap">
                      <Icon name="settings" size={14} color="#14b8a6" className="mform__input-icon" />
                      <select className="mform__select mform__input--with-icon" name="isActive" value={String(form.isActive)} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.value === 'true' }))}>
                        <option value="true">{t('common.active')}</option>
                        <option value="false">{t('common.inactive')}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mform__row mform__row--4">
                  <div className="mform__field mform__field--span2">
                    <label className="mform__label">{t('departments.lblParent')}</label>
                    <div className="mform__input-wrap">
                      <Icon name="filter" size={14} color="#8b5cf6" className="mform__input-icon" />
                      <select className="mform__select mform__input--with-icon" name="parentDepartmentId" value={form.parentDepartmentId} onChange={handleChange} disabled={!form.branchId}>
                        <option value="">{t('departments.noneParent')}</option>
                        {parentCandidates.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="mform__field mform__field--span2">
                    <label className="mform__label">{t('departments.lblMgr')}</label>
                    <div className="mform__input-wrap">
                      <Icon name="user" size={14} color="#02c3a6" className="mform__input-icon" />
                      <select className="mform__select mform__input--with-icon" name="managerUserId" value={form.managerUserId} onChange={handleChange} disabled={!form.branchId}>
                        <option value="">—</option>
                        {managerOpts.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mform__row mform__row--4">
                  <div className="mform__field mform__field--span4">
                    <label className="mform__label">{t('departments.lblDesc')}</label>
                    <div className="mform__input-wrap">
                      <Icon name="report" size={14} color="#8b5cf6" className="mform__input-icon" style={{ top: 22, transform: 'none' }} />
                      <textarea className="mform__textarea mform__input--with-icon" name="description" rows={4} value={form.description} onChange={handleChange} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
